/**
 * Admin Dashboard V2 - My Riad Manager Administration
 * 
 * Refonte UX orientée:
 * - Alertes critiques actionnables en priorité
 * - KPIs clairs avec hiérarchie visuelle
 * - Actions rapides accessibles
 * - Réduction de la densité inutile
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Building2, CreditCard, TrendingUp, AlertTriangle,
  Clock, CheckCircle, XCircle, ArrowRight, RefreshCw,
  DollarSign, Calendar, Activity, Bell, FileText, 
  ChevronRight, ExternalLink, Zap, AlertCircle, 
  TrendingDown, Shield, Eye, Play
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { api } from '../../App';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BRANDING } from '../../config/branding';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceAlerts, setInvoiceAlerts] = useState({ overdue: 0, total_overdue: 0 });

  useEffect(() => {
    fetchDashboard();
    fetchInvoiceAlerts();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/dashboard');
      setData(data);
    } catch (error) {
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceAlerts = async () => {
    try {
      const { data } = await api.get('/admin/invoices/stats');
      setInvoiceAlerts({
        overdue: data.by_status?.OVERDUE?.count || 0,
        total_overdue: data.overdue_total || 0
      });
    } catch (error) {
      console.error('Error fetching invoice alerts');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-violet-600 mx-auto" />
          <p className="mt-4 text-muted-foreground">Chargement du cockpit...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { business, operational, support } = data;

  // Build prioritized alerts
  const alerts = [];
  
  // CRITICAL - Factures en retard
  if (invoiceAlerts.overdue > 0) {
    alerts.push({
      priority: 1,
      severity: 'critical',
      icon: FileText,
      title: 'Factures en retard',
      value: invoiceAlerts.overdue,
      detail: `${invoiceAlerts.total_overdue.toLocaleString('fr-FR')} MAD impayés`,
      action: () => navigate('/admin/billing'),
      actionLabel: 'Voir facturation',
      color: 'red'
    });
  }
  
  // CRITICAL - Abonnements expirés
  if (business.subscriptions.expired > 0) {
    alerts.push({
      priority: 1,
      severity: 'critical',
      icon: XCircle,
      title: 'Abonnements expirés',
      value: business.subscriptions.expired,
      detail: 'Clients sans accès',
      action: () => navigate('/admin/subscriptions'),
      actionLabel: 'Gérer',
      color: 'red'
    });
  }

  // CRITICAL - Comptes suspendus
  if (support.suspended_accounts?.length > 0) {
    alerts.push({
      priority: 1,
      severity: 'critical',
      icon: AlertTriangle,
      title: 'Comptes suspendus',
      value: support.suspended_accounts.length,
      detail: support.suspended_accounts.slice(0, 2).map(a => a.name).join(', '),
      action: () => navigate('/admin/customers'),
      actionLabel: 'Voir clients',
      color: 'orange'
    });
  }
  
  // HIGH - Expirations dans 7 jours
  const expiring7Days = support.expiring_subscriptions?.filter(s => s.days_remaining <= 7) || [];
  if (expiring7Days.length > 0) {
    alerts.push({
      priority: 2,
      severity: 'high',
      icon: Clock,
      title: 'Expirations dans 7 jours',
      value: expiring7Days.length,
      detail: expiring7Days.slice(0, 2).map(s => s.account_name).join(', '),
      action: () => navigate('/admin/subscriptions'),
      actionLabel: 'Renouveler',
      color: 'amber'
    });
  }
  
  // MEDIUM - Essais se terminant
  if (support.trials_ending_soon?.length > 0) {
    alerts.push({
      priority: 3,
      severity: 'medium',
      icon: Calendar,
      title: 'Essais se terminant',
      value: support.trials_ending_soon.length,
      detail: 'Opportunités de conversion',
      action: () => navigate('/admin/subscriptions'),
      actionLabel: 'Convertir',
      color: 'blue'
    });
  }

  // Sort by priority
  alerts.sort((a, b) => a.priority - b.priority);
  
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const highCount = alerts.filter(a => a.severity === 'high').length;

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
            Administration
          </h1>
          <p className="text-muted-foreground mt-1">
            Vue d'ensemble • {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchDashboard} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* SECTION 1: Alertes Critiques */}
      {alerts.length > 0 && (
        <section data-testid="alerts-section">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${criticalCount > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
              <Bell className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Actions Requises</h2>
              <p className="text-sm text-muted-foreground">
                {criticalCount > 0 && <span className="text-red-600 font-medium">{criticalCount} critique(s)</span>}
                {criticalCount > 0 && highCount > 0 && ' • '}
                {highCount > 0 && <span className="text-amber-600">{highCount} important(s)</span>}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alerts.slice(0, 6).map((alert, idx) => (
              <Card 
                key={idx}
                className={`cursor-pointer transition-all hover:shadow-lg border-l-4 ${
                  alert.severity === 'critical' ? 'border-l-red-500 hover:border-l-red-600' :
                  alert.severity === 'high' ? 'border-l-amber-500 hover:border-l-amber-600' :
                  'border-l-blue-500 hover:border-l-blue-600'
                }`}
                onClick={alert.action}
                data-testid={`alert-card-${idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        alert.color === 'red' ? 'bg-red-100' :
                        alert.color === 'orange' ? 'bg-orange-100' :
                        alert.color === 'amber' ? 'bg-amber-100' :
                        'bg-blue-100'
                      }`}>
                        <alert.icon className={`w-5 h-5 ${
                          alert.color === 'red' ? 'text-red-600' :
                          alert.color === 'orange' ? 'text-orange-600' :
                          alert.color === 'amber' ? 'text-amber-600' :
                          'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold">{alert.title}</p>
                        <p className="text-2xl font-bold mt-1">{alert.value}</p>
                        <p className="text-sm text-muted-foreground mt-1">{alert.detail}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* SECTION 2: KPIs Principaux */}
      <section>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-violet-600" />
          Performance Business
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Revenue */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Revenus Mensuels</p>
                  <p className="text-3xl font-bold text-green-700 mt-2">
                    {business.revenue.monthly.toLocaleString('fr-FR')}
                  </p>
                  <p className="text-sm text-green-600 mt-1">{business.revenue.currency}</p>
                </div>
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-green-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-sm text-green-600">
                  <span className="font-medium">{business.subscriptions.active}</span> abonnements actifs
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Clients */}
          <Card 
            className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/customers')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-violet-600 font-medium">Clients SaaS</p>
                  <p className="text-3xl font-bold text-violet-700 mt-2">
                    {business.accounts.total}
                  </p>
                  <p className="text-sm text-violet-600 mt-1">{business.accounts.active} actifs</p>
                </div>
                <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-violet-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-violet-200 flex justify-between text-sm">
                <span className="text-green-600">+{business.accounts.new_this_month} ce mois</span>
                <span className="text-violet-600 flex items-center gap-1">
                  Voir <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Riads */}
          <Card 
            className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/riads')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Riads</p>
                  <p className="text-3xl font-bold text-blue-700 mt-2">
                    {business.riads.total}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">{business.riads.active} actifs</p>
                </div>
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200 flex justify-between text-sm">
                <span className="text-green-600">+{business.riads.new_this_month} ce mois</span>
                <span className="text-blue-600 flex items-center gap-1">
                  Gérer <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Abonnements */}
          <Card 
            className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/subscriptions')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Abonnements</p>
                  <p className="text-3xl font-bold text-amber-700 mt-2">
                    {business.subscriptions.active}
                  </p>
                  <p className="text-sm text-amber-600 mt-1">actifs</p>
                </div>
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-7 h-7 text-amber-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-200 flex justify-between text-sm">
                {business.subscriptions.trial > 0 && (
                  <span className="text-blue-600">{business.subscriptions.trial} en essai</span>
                )}
                {business.subscriptions.expiring_soon > 0 && (
                  <span className="text-orange-600">{business.subscriptions.expiring_soon} expirent</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SECTION 3: Répartition & Activité */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution par Plan */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-violet-600" />
              Répartition par Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {business.by_plan?.map((plan, idx) => {
                const percentage = business.subscriptions.active > 0 
                  ? Math.round((plan.count / business.subscriptions.active) * 100) 
                  : 0;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: plan.plan_color || '#8B5CF6' }}
                        />
                        <span className="font-medium">{plan.plan_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{plan.count}</span>
                        <span className="text-muted-foreground">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: plan.plan_color || '#8B5CF6'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!business.by_plan || business.by_plan.length === 0) && (
                <p className="text-muted-foreground text-center py-4">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Santé Opérationnelle */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-violet-600" />
              Santé Opérationnelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Setup Complet</span>
                </div>
                <p className="text-2xl font-bold text-green-700 mt-2">
                  {operational?.completed_setup_count || 0}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 text-blue-600">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">En cours</span>
                </div>
                <p className="text-2xl font-bold text-blue-700 mt-2">
                  {operational?.incomplete_setup_count || 0}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Config Issues</span>
                </div>
                <p className="text-2xl font-bold text-amber-700 mt-2">
                  {operational?.config_issues_count || 0}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-violet-50 border border-violet-100">
                <div className="flex items-center gap-2 text-violet-600">
                  <Users className="w-5 h-5" />
                  <span className="font-medium">Conciergeries</span>
                </div>
                <p className="text-2xl font-bold text-violet-700 mt-2">
                  {data.concierges?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 4: Actions Rapides & Support */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Actions Rapides */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-violet-600" />
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/admin/customers')}
            >
              <div className="p-1.5 bg-violet-100 rounded-lg">
                <Users className="w-4 h-4 text-violet-600" />
              </div>
              <span>Nouveau Client</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/admin/billing')}
            >
              <div className="p-1.5 bg-green-100 rounded-lg">
                <FileText className="w-4 h-4 text-green-600" />
              </div>
              <span>Générer Factures</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/admin/backups')}
            >
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <span>Backup Manuel</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/admin/audit')}
            >
              <div className="p-1.5 bg-amber-100 rounded-lg">
                <Activity className="w-4 h-4 text-amber-600" />
              </div>
              <span>Voir Audit Logs</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
          </CardContent>
        </Card>

        {/* Essais se terminant */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Essais se Terminant
            </CardTitle>
            <CardDescription>Opportunités de conversion</CardDescription>
          </CardHeader>
          <CardContent>
            {support.trials_ending_soon?.length > 0 ? (
              <div className="space-y-3">
                {support.trials_ending_soon.slice(0, 4).map((trial, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/customers/${trial.account_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{trial.account_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Fin: {format(new Date(trial.trial_end_date), 'dd/MM/yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={
                        trial.days_remaining <= 3 ? 'text-red-600 border-red-600' :
                        trial.days_remaining <= 7 ? 'text-orange-600 border-orange-600' :
                        'text-blue-600 border-blue-600'
                      }>
                        J-{trial.days_remaining}
                      </Badge>
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700">
                        Convertir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Aucun essai se terminant bientôt</p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default AdminDashboard;
